import { DateTime } from 'luxon'
import {
  cancellationDeadlineAt,
  EMPTY_HISTORY,
  GetHistoryResponse,
  HistoryEntry,
  ManagedReservationDetail,
  ReservationAuditField,
  reservationConsumesQuota,
  reservationState,
  sameEmail,
} from '@seiri/shared/common'
import {
  EMPTY_VALUE,
  HistoryChangeField,
  historyActorText,
  historyChangesText,
  legacyStampShown,
  plainHistoryValue,
} from './history'

const at = (value: Date | string, timezone: string) =>
  DateTime.fromJSDate(new Date(value)).setZone(timezone).setLocale('sk')

/** When the reservation happens, in the premise's timezone: "piatok 9. 10. 2026 · 10:00 – 11:00". */
export function reservationWhen(reservation: { start: Date | string; end: Date | string }, timezone: string): string {
  const start = at(reservation.start, timezone)
  const end = at(reservation.end, timezone)
  const endText = end.hasSame(start, 'day') ? end.toFormat('HH:mm') : end.toFormat('d. M. yyyy HH:mm')
  return `${start.toFormat('cccc d. M. yyyy')} · ${start.toFormat('HH:mm')} – ${endText}`
}

/** "60 min", "1 h 30 min", "2 h". */
export function reservationDuration(reservation: { start: Date | string; end: Date | string }): string {
  const minutes = Math.round((new Date(reservation.end).getTime() - new Date(reservation.start).getTime()) / 60_000)
  if (minutes <= 60 || (minutes < 90 && minutes % 60 !== 0)) {
    return `${minutes} min`
  }
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours} h ${rest} min` : `${hours} h`
}

/**
 * The last moment the customer may cancel, for their own page, in the premise's zone: "15. 10. 2026 17:00". `null`
 * when they may never cancel.
 */
export function customerCancelUntil(
  terms: { start: Date | string; cancellationDeadline: number },
  timezone: string,
): string | null {
  const deadline = cancellationDeadlineAt(terms)
  return deadline ? at(deadline, timezone).toFormat('d. M. yyyy HH:mm') : null
}

/**
 * The rule alone, for an event whose sign-ups each copy it: "Do 24 h pred začiatkom", "Kedykoľvek do začiatku",
 * "Klient nemôže zrušiť sám".
 */
export function cancellationDeadlineRule(minutes: number): string {
  if (minutes === -1) {
    return 'Klient nemôže zrušiť sám'
  }
  if (minutes === 0) {
    return 'Kedykoľvek do začiatku'
  }
  return `Do ${minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`} pred začiatkom`
}

/** What the customer may do with their own link: "do 24 h pred začiatkom (do 8. 10. 10:00)". */
export function cancellationDeadlineText(
  reservation: { start: Date | string; cancellationDeadline: number },
  timezone: string,
): string {
  const deadline = reservation.cancellationDeadline
  if (deadline === -1) {
    return 'klient ju nemôže zrušiť sám'
  }
  if (deadline === 0) {
    return 'kedykoľvek do začiatku'
  }
  const until = at(reservation.start, timezone).minus({ minutes: deadline }).toFormat('d. M. HH:mm')
  const amount = deadline % 60 === 0 ? `${deadline / 60} h` : `${deadline} min`
  return `do ${amount} pred začiatkom (do ${until})`
}

/**
 * The status line under the reservation's row: what its state means for the manager, and why it can no longer change
 * (docs/reservations.md).
 */
export function reservationStatusSentence(reservation: ManagedReservationDetail, now: Date, timezone: string): string {
  switch (reservationState(reservation, now)) {
    case 'SCHEDULED':
      // A manual reservation's customer has the link only once staff e-mail them, and since 2026-10-15 staff choose
      // whether that link may cancel.
      if (reservation.cancellationDeadline === -1) {
        return reservation.source === 'manual'
          ? 'Rezervácia je naplánovaná. Pridali ju ručne, klient ju nemôže zrušiť sám.'
          : 'Rezervácia je naplánovaná. Klient ju nemôže zrušiť sám.'
      }
      return reservation.source === 'manual'
        ? `Rezervácia je naplánovaná. Pridali ju ručne. Ak klienta upozorníte e-mailom, môže ju sám zrušiť cez odkaz ${cancellationDeadlineText(reservation, timezone)}.`
        : `Rezervácia je naplánovaná. Klient ju môže sám zrušiť cez odkaz v e-maile ${cancellationDeadlineText(reservation, timezone)}.`
    case 'ONGOING':
      return 'Rezervácia práve prebieha. Od začiatku sa už nedá upraviť ani zrušiť.'
    case 'COMPLETED':
      return reservation.source === 'manual'
        ? 'Rezervácia sa uskutočnila. Bola pridaná ručne. Po začiatku sa už nedá upraviť.'
        : 'Rezervácia sa uskutočnila. Po začiatku sa už nedá upraviť.'
    case 'CANCELLED': {
      const when = reservation.cancelledAt ? at(reservation.cancelledAt, timezone).toFormat("d. M. 'o' HH:mm") : ''
      const who =
        reservation.cancelledBy?.kind === 'customer'
          ? ' klientom cez odkaz v e-maile'
          : reservation.cancelledBy
            ? ` (${reservation.cancelledBy.name})`
            : ''
      // Freed by the customer's own cancel before the start, or by staff's at least 12 h ahead (docs/billing.md).
      const freed = !reservationConsumesQuota({
        start: reservation.start,
        cancelledAt: reservation.cancelledAt,
        cancelledByCustomer: reservation.cancelledBy?.kind === 'customer',
      })
      const quota = freed ? ' Zrušená včas, preto sa nezapočítava do mesačného limitu rezervácií.' : ''
      return `Rezervácia bola zrušená ${when}${who}. Termín je znova voľný.${quota}`
    }
  }
}

/** One line of the História card. */
export interface ReservationHistoryItem {
  at: Date
  text: string
  detail: string
}

/** How a reservation's edit is told on História: the form's labels, the values where they help (docs/wording.md). */
export function reservationChangeFields(timezone: string): Record<ReservationAuditField, HistoryChangeField> {
  const instant = (value: unknown) =>
    typeof value === 'string' ? at(value, timezone).toFormat("d. M. 'o' HH:mm") : EMPTY_VALUE
  return {
    name: { label: 'názov', value: plainHistoryValue },
    start: { label: 'začiatok', value: instant },
    end: { label: 'koniec', value: instant },
    place: { label: 'miesto', value: plainHistoryValue },
    activity: { label: 'činnosť', value: plainHistoryValue },
    employees: { label: 'zamestnanci', value: plainHistoryValue },
    description: { label: 'popis' },
    note: { label: 'interná poznámka' },
  }
}

/** One audit entry of the reservation as a História item; null for a kind the card does not show. */
function reservationAuditItem(entry: HistoryEntry, timezone: string): ReservationHistoryItem | null {
  const when = at(entry.at, timezone).toFormat("d. M. 'o' HH:mm")
  const by = historyActorText(entry.actor)
  switch (entry.kind) {
    case 'reservation.edited':
      return {
        at: new Date(entry.at),
        text: by ? `Upravená: ${by}` : 'Upravená',
        detail: [when, historyChangesText(entry.data.changes, reservationChangeFields(timezone))]
          .filter(Boolean)
          .join(' · '),
      }
    case 'reservation.cancelled': {
      const customer = entry.actor.kind === 'customer'
      return {
        at: new Date(entry.at),
        text: customer ? `Zrušená klientom: ${entry.customer ?? ''}`.trim() : by ? `Zrušená: ${by}` : 'Zrušená',
        detail: [when, customer ? 'cez odkaz v e-maile' : '', entry.data.reason ? `dôvod: ${entry.data.reason}` : '']
          .filter(Boolean)
          .join(' · '),
      }
    }
    default:
      return null
  }
}

/**
 * What is known to have happened, newest first: the booking (and, for a web booking, the two e-mails that always go
 * with it) from the row, and from the audit log (docs/audit.md) each edit with what changed and the cancellation.
 * Before the reservation's first audit row, its last edit and its cancellation come from their stamps
 * (`legacyStampShown`), which record neither what changed nor more than the latest. Which e-mails staff chose to send
 * is not recorded yet; that needs the communication module's message log.
 */
export function reservationHistory(
  reservation: ManagedReservationDetail,
  timezone: string,
  audit: GetHistoryResponse = EMPTY_HISTORY,
): ReservationHistoryItem[] {
  const when = (value: Date | string) => at(value, timezone).toFormat("d. M. 'o' HH:mm")
  const name = (person?: { firstName?: string; lastName?: string } | null) =>
    person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() : ''
  const created = new Date(reservation.createdAt)
  const items: ReservationHistoryItem[] = []

  if (reservation.source === 'public') {
    const booker = name(reservation.booker) || name(reservation.customer)
    items.push(
      { at: created, text: `Rezervácia cez web: ${booker}`, detail: `${when(created)} · verejná stránka prevádzky` },
      {
        at: created,
        text: 'Potvrdenie odoslané klientovi (e-mail)',
        detail: `${when(created)} · s odkazom na rezerváciu`,
      },
      { at: created, text: 'Upozornenie odoslané prevádzke (e-mail)', detail: when(created) },
    )
  } else {
    items.push({
      at: created,
      text: `Rezervácia vytvorená ručne${reservation.createdBy ? `: ${name(reservation.createdBy)}` : ''}`,
      detail: when(created),
    })
  }

  const cancelled = reservation.cancelledAt ? new Date(reservation.cancelledAt) : null
  if (reservation.updatedAt) {
    const updated = new Date(reservation.updatedAt)
    const isCancel = cancelled && Math.abs(updated.getTime() - cancelled.getTime()) < 60_000
    if (
      updated.getTime() - created.getTime() > 60_000 &&
      !isCancel &&
      legacyStampShown(updated, reservation.id, audit)
    ) {
      items.push({
        at: updated,
        text: `Upravená${reservation.updatedBy ? `: ${name(reservation.updatedBy)}` : ''}`,
        detail: `${when(updated)} · naposledy`,
      })
    }
  }

  if (cancelled && legacyStampShown(cancelled, reservation.id, audit)) {
    const by = reservation.cancelledBy
    items.push({
      at: cancelled,
      text: by?.kind === 'customer' ? `Zrušená klientom: ${by.name}` : `Zrušená${by ? `: ${by.name}` : ''}`,
      detail: [
        when(cancelled),
        by?.kind === 'customer' ? 'cez odkaz v e-maile' : '',
        reservation.cancellationReason ? `dôvod: ${reservation.cancellationReason}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    })
  }

  for (const entry of audit.entries) {
    const item = entry.subjectId === reservation.id ? reservationAuditItem(entry, timezone) : null
    if (item) {
      items.push(item)
    }
  }
  return items.sort((a, b) => b.at.getTime() - a.at.getTime())
}

/** A person as either record describes them: the client profile or the booking form's snapshot. */
export interface PersonDetails {
  firstName: string
  lastName: string
  email: string
  phone: string
}

/**
 * How one value typed into the booking form compares with the client profile: `different`, or `missingInProfile`
 * when the form has a value and the profile does not. A field that matches, or that the form left empty, is absent.
 */
export type FormDataDifference = 'different' | 'missingInProfile'

export type FormDataDifferences = Partial<Record<'name' | 'email' | 'phone', FormDataDifference>>

const normalizeName = (person: PersonDetails) =>
  `${person.firstName} ${person.lastName}`.trim().replace(/\s+/g, ' ').toLocaleLowerCase('sk')
/** "+421 905 123 456", "+421-905/123456" and "+421905123456" are one number; "0905…" stays another. */
const normalizePhone = (phone: string) => phone.replace(/[\s\-/().]/g, '')

/**
 * What the booking form said differently from the client profile (docs/clients.md: the profile is the premise's
 * record and a form never updates it, so the two drift apart). Names ignore case and spacing, e-mails case, phones
 * separators. Only a hint for the manager; nothing acts on it.
 */
export function formDataDifferences(profile: PersonDetails | null, form: PersonDetails): FormDataDifferences {
  const differences: FormDataDifferences = {}
  const compare = (
    field: keyof FormDataDifferences,
    formValue: string,
    profileValue: string,
    same: (a: string, b: string) => boolean,
  ) => {
    if (!formValue.trim()) {
      return
    }
    if (!profileValue.trim()) {
      differences[field] = 'missingInProfile'
    } else if (!same(formValue, profileValue)) {
      differences[field] = 'different'
    }
  }
  const empty = { firstName: '', lastName: '', email: '', phone: '' }
  const p = profile ?? empty
  compare('name', normalizeName(form), normalizeName(p), (a, b) => a === b)
  compare('email', form.email, p.email, sameEmail)
  compare('phone', normalizePhone(form.phone), normalizePhone(p.phone), (a, b) => a === b)
  return differences
}
